<div class="api-integration-section" *ngIf="form" [formGroup]="form">
  <div class="form-fields">
    <!-- Row 1: Data Source Name and API Base URL -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="data-source-name" class="form-label">
          Data Source Name <span class="required">*</span>
        </label>
        <input
          type="text"
          id="data-source-name"
          formControlName="dataSourceName"
          class="form-input"
          placeholder="Enter data source name"
          [disabled]="readonly"
        />
        <div
          *ngIf="showValidation && !readonly && form.get('dataSourceName')?.invalid && form.get('dataSourceName')?.touched"
          class="error-message"
        >
          Data Source Name is required
        </div>
      </div>

      <div class="form-field col-6">
        <label for="api-base-url" class="form-label">
          API Base URL <span class="required">*</span>
        </label>
        <input
          type="text"
          id="api-base-url"
          formControlName="apiBaseUrl"
          class="form-input"
          placeholder="Enter API base URL"
          [disabled]="readonly"
        />
        <div
          *ngIf="showValidation && !readonly && form.get('apiBaseUrl')?.invalid && form.get('apiBaseUrl')?.touched"
          class="error-message"
        >
          API Base URL is required
        </div>
      </div>
    </div>

    <!-- Row 2: Endpoint Path and HTTP Method -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="endpoint-path" class="form-label">
          Endpoint Path <span class="required">*</span>
        </label>
        <input
          type="text"
          id="endpoint-path"
          formControlName="endpointPath"
          class="form-input"
          placeholder="Enter endpoint path"
          [disabled]="readonly"
        />
        <div
          *ngIf="showValidation && !readonly && form.get('endpointPath')?.invalid && form.get('endpointPath')?.touched"
          class="error-message"
        >
          Endpoint Path is required
        </div>
      </div>

      <div class="form-field col-6">
        <label for="http-method" class="form-label">
          HTTP Method <span class="required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <button
            type="button"
            class="custom-dropdown-trigger"
            [class.error]="showValidation && !readonly && form.get('httpMethod')?.invalid && form.get('httpMethod')?.touched"
            [class.open]="isHttpMethodDropdownOpen"
            (click)="toggleHttpMethodDropdown()"
            [disabled]="readonly"
          >
            <div class="trigger-content">
              <span *ngIf="!form.get('httpMethod')?.value" class="placeholder">Select HTTP Method</span>
              <span *ngIf="form.get('httpMethod')?.value" class="selected-value">
                {{ getSelectedHttpMethod() }}
              </span>
              <svg class="dropdown-arrow" [class.open]="isHttpMethodDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </button>
          <div class="custom-dropdown-panel" *ngIf="isHttpMethodDropdownOpen">
            <div
              *ngFor="let method of httpMethodOptions"
              class="dropdown-option"
              [class.selected]="isHttpMethodSelected(method)"
              (click)="selectHttpMethod(method)"
            >
              <div class="checkbox-wrapper">
                <div class="checkbox-custom" [class.checked]="isHttpMethodSelected(method)"></div>
              </div>
              <span class="option-label">{{ method }}</span>
            </div>
          </div>
        </div>
        <div
          *ngIf="showValidation && !readonly && form.get('httpMethod')?.invalid && form.get('httpMethod')?.touched"
          class="error-message"
        >
          HTTP Method is required
        </div>
      </div>
    </div>

    <!-- Row 3: Custom Headers and Query Parameters -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="custom-headers" class="form-label">
          Custom Headers
        </label>
        <textarea
          id="custom-headers"
          formControlName="customHeaders"
          class="form-textarea"
          rows="3"
          placeholder="Optional JSON object for extra HTTP headers, such as Authorization or Accept"
          [disabled]="readonly"
        ></textarea>
      </div>
      <div class="form-field col-6">
        <label for="query-parameters" class="form-label">
          Query Parameters
        </label>
        <textarea
          id="query-parameters"
          formControlName="queryParameters"
          class="form-textarea"
          rows="3"
          placeholder="Optional JSON object for URL parameters, such as limit, offset, or filters"
          [disabled]="readonly"
        ></textarea>
      </div>
    </div>

    <!-- Row 4: Body Payload and Authentication Details -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="body-payload" class="form-label">
          Body Payload
        </label>
        <textarea
          id="body-payload"
          formControlName="bodyPayload"
          class="form-textarea"
          rows="3"
          placeholder="Optional JSON request body for POST, PUT, or PATCH; leave empty for GET"
          [disabled]="readonly"
        ></textarea>
      </div>
      <div class="form-field col-6">
        <label for="authentication-details" class="form-label">
          Authentication Details
        </label>
        <textarea
          id="authentication-details"
          formControlName="authenticationDetails"
          class="form-textarea"
          rows="3"
          placeholder="Enter authentication details"
          [disabled]="readonly"
        ></textarea>
      </div>
    </div>

    <!-- Row 5: Authentication Type and Response Format -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="authentication-type" class="form-label">
          Authentication Type <span class="required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <button
            type="button"
            class="custom-dropdown-trigger"
            [class.error]="showValidation && !readonly && form.get('authenticationType')?.invalid && form.get('authenticationType')?.touched"
            [class.open]="isAuthenticationTypeDropdownOpen"
            (click)="toggleAuthenticationTypeDropdown()"
            [disabled]="readonly"
          >
            <div class="trigger-content">
              <span *ngIf="!form.get('authenticationType')?.value" class="placeholder">Select Authentication Type</span>
              <span *ngIf="form.get('authenticationType')?.value" class="selected-value">
                {{ getSelectedAuthenticationType() }}
              </span>
              <svg class="dropdown-arrow" [class.open]="isAuthenticationTypeDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </button>
          <div class="custom-dropdown-panel" *ngIf="isAuthenticationTypeDropdownOpen">
            <div
              *ngFor="let type of authenticationTypeOptions"
              class="dropdown-option"
              [class.selected]="isAuthenticationTypeSelected(type)"
              (click)="selectAuthenticationType(type)"
            >
              <div class="checkbox-wrapper">
                <div class="checkbox-custom" [class.checked]="isAuthenticationTypeSelected(type)">
                  <svg *ngIf="isAuthenticationTypeSelected(type)" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <span class="option-label">{{ type }}</span>
            </div>
          </div>
        </div>
        <div
          *ngIf="showValidation && !readonly && form.get('authenticationType')?.invalid && form.get('authenticationType')?.touched"
          class="error-message"
        >
          Authentication Type is required
        </div>
      </div>

      <div class="form-field col-6">
        <label for="response-format" class="form-label">
          Response Format <span class="required">*</span>
        </label>
        <div class="custom-dropdown-wrapper">
          <button
            type="button"
            class="custom-dropdown-trigger"
            [class.error]="showValidation && !readonly && form.get('responseFormat')?.invalid && form.get('responseFormat')?.touched"
            [class.open]="isResponseFormatDropdownOpen"
            (click)="toggleResponseFormatDropdown()"
            [disabled]="readonly"
          >
            <div class="trigger-content">
              <span *ngIf="!form.get('responseFormat')?.value" class="placeholder">Select Response Format</span>
              <span *ngIf="form.get('responseFormat')?.value" class="selected-value">
                {{ getSelectedResponseFormat() }}
              </span>
              <svg class="dropdown-arrow" [class.open]="isResponseFormatDropdownOpen" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </button>
          <div class="custom-dropdown-panel" *ngIf="isResponseFormatDropdownOpen">
            <div
              *ngFor="let format of responseFormatOptions"
              class="dropdown-option"
              [class.selected]="isResponseFormatSelected(format)"
              (click)="selectResponseFormat(format)"
            >
              <div class="checkbox-wrapper">
                <div class="checkbox-custom" [class.checked]="isResponseFormatSelected(format)">
                  <svg *ngIf="isResponseFormatSelected(format)" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <span class="option-label">{{ format }}</span>
            </div>
          </div>
        </div>
        <div
          *ngIf="showValidation && !readonly && form.get('responseFormat')?.invalid && form.get('responseFormat')?.touched"
          class="error-message"
        >
          Response Format is required
        </div>
      </div>
    </div>

    <!-- Row 6: Data Path in Response and Expected Pull Frequency -->
    <div class="form-row">
      <div class="form-field col-6">
        <label for="data-path-in-response" class="form-label">
          Data Path in Response <span class="required">*</span>
        </label>
        <input
          type="text"
          id="data-path-in-response"
          formControlName="dataPathInResponse"
          class="form-input"
          placeholder="Enter data path (e.g., $.data.items)"
          [disabled]="readonly"
        />
        <div
          *ngIf="showValidation && !readonly && form.get('dataPathInResponse')?.invalid && form.get('dataPathInResponse')?.touched"
          class="error-message"
        >
          Data Path in Response is required
        </div>
      </div>
      <div class="form-field col-6">
        <label for="expected-pull-frequency" class="form-label">
          Expected Pull Frequency <span class="required">*</span>
        </label>
        <input
          type="text"
          id="expected-pull-frequency"
          formControlName="expectedPullFrequency"
          class="form-input"
          placeholder="Enter pull frequency (e.g., daily, weekly, monthly)"
          [disabled]="readonly"
        />
        <div
          *ngIf="showValidation && !readonly && form.get('expectedPullFrequency')?.invalid && form.get('expectedPullFrequency')?.touched"
          class="error-message"
        >
          Expected Pull Frequency is required
        </div>
      </div>
    </div>

    <!-- Additional Notes -->
    <div class="form-field">
      <label for="additional-notes" class="form-label">
        Additional Notes
      </label>
      <textarea
        id="additional-notes"
        formControlName="additionalNotes"
        class="form-textarea"
        rows="4"
        placeholder="Additional notes about the API integration"
        [disabled]="readonly"
      ></textarea>
    </div>
  </div>
</div>

